import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';

const ReturnInventoryDialog = ({
  open,
  onClose,
  onSubmit,
  assignment,
  isLoading = false,
}) => {
  const [returnData, setReturnData] = useState({
    return_date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    return_remarks: '',
  });

  // Reset form data when dialog opens/closes or assignment changes
  useEffect(() => {
    if (open && assignment) {
      setReturnData({
        return_date: new Date().toISOString().split('T')[0],
        return_remarks: '', // Start with empty remarks since we'll append to existing ones
      });
    }
  }, [open, assignment]);

  const handleInputChange = (field, value) => {
    setReturnData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit(returnData);
  };

  const handleClose = () => {
    setReturnData({
      return_date: new Date().toISOString().split('T')[0],
      return_remarks: '',
    });
    onClose();
  };

  if (!assignment) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AssignmentReturnIcon color="success" />
          Return Inventory Item
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Assignment Details */}
          <Typography variant="h6" gutterBottom>
            Assignment Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Employee:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.assigned_to?.full_name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Item:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.inventory_item?.name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Quantity:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.quantity || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Assigned Date:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.assigned_date ? new Date(assignment.assigned_date).toLocaleDateString() : 'N/A'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Return Form */}
          <Typography variant="h6" gutterBottom>
            Return Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Return Date"
                type="date"
                value={returnData.return_date}
                onChange={(e) => handleInputChange('return_date', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0], // Can't select future dates
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                multiline
                rows={4}
                value={returnData.return_remarks}
                onChange={(e) => handleInputChange('return_remarks', e.target.value)}
                placeholder="Add any return notes or comments..."
                helperText={
                  assignment.remarks 
                    ? "Return notes will be appended to existing remarks."
                    : "Optional return notes or comments."
                }
              />
            </Grid>
          </Grid>

          {/* Show existing remarks if any */}
          {assignment.remarks && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Existing Remarks:
              </Typography>
              <Box 
                sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {assignment.remarks}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          color="inherit"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          startIcon={<AssignmentReturnIcon />}
          disabled={isLoading || !returnData.return_date}
        >
          {isLoading ? 'Returning...' : 'Return Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ReturnInventoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  assignment: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default ReturnInventoryDialog;